import type { ReactNode } from "react";
import type { CardOutcome } from "../lib/outcome";
import { Tick } from "./Illustration/Tick";
import { Magnifier } from "./Illustration/Magnifier";
import { Page } from "./Illustration/Page";
import { SoftPhoto } from "./Illustration/SoftPhoto";

/**
 * The outcome vocabulary is `lib/outcome.ts` and this card does not get one of
 * its own: two unions with the same name and different members is how a screen
 * ends up unable to hand `outcomeOf(page)` straight to the card. Importing the
 * type is not the layering leak that re-exporting a store would be, because
 * `outcome.ts` is DOM free and holds no state; it is the vocabulary.
 *
 * Of the nine, seven wear a card. `not-read` and `reading` are states rather
 * than outcomes and get the error and loading treatments instead.
 *
 * **The sixth and seventh are `model-marked` and `corpus-marked`, and both
 * wear the same card as the others.**
 * That is the contract's rule and it is a layout rule before it is a copy
 * rule: an admission and a finding are indistinguishable at a glance, and a
 * page our reader marked is nearer an admission than any of the others. It
 * gets the same frame, the same type sizes and the same cost badge, and what
 * separates it is what it says. `OUTCOMES` below stays at five, because that
 * list is what the marketing pages promise and this is the state a question
 * sits in until its library is written.
 */
export type { CardOutcome };

export interface OutcomeCardProps {
  outcome: CardOutcome;
  /** Overrides the standing description when a real page has its own sentence. */
  children?: ReactNode;
  /** Shown under the title on a result screen, for example "17 of 20". */
  detail?: string;
  /** What THIS page cost the learner looking at it, in the words a parent
   *  reads: "1 credit" or "free".
   *
   *  The badge below is a rule about outcomes, and a rule about outcomes
   *  cannot answer a question about money. The same marked page is served to
   *  every learner who sends the same bytes and only the first of them paid
   *  for it, so a row in a sibling's list wore "1 credit" against a balance
   *  that had never moved. C-A found it on the result footer, C-B found it on
   *  the Home and Pages rows, and it is one prop because it is one defect.
   *
   *  Null and undefined are different answers. Undefined is a caller with
   *  nothing to say about this page, and the standing badge stands. Null is a
   *  caller that looked and found the service silent, and then the card shows
   *  no cost at all rather than a number nobody can show was taken. */
  cost?: string | null;
}

interface OutcomeCopy {
  title: string;
  cost: string;
  body: string;
  mark: ReactNode;
}

const copy: Record<CardOutcome, OutcomeCopy> = {
  explained: {
    title: "Marked and explained",
    cost: "1 credit",
    body: "We marked every answer, and where something went wrong we named the cause.",
    mark: <Tick size={28} />,
  },
  "no-cause": {
    title: "Marked, one cause not found",
    cost: "1 credit",
    body: "We marked every answer. For one of them we can say the answer is wrong, but not why.",
    mark: <Magnifier size={28} />,
  },
  /* The honesty rule holds through the label. The title says who marked it and
     what has not happened yet, in that order, because "not yet verified" alone
     reads as a warning about the child's work rather than about ours. */
  "model-marked": {
    title: "Marked by our reader, not yet verified",
    cost: "free",
    body: "We have not written a marking library for this kind of question yet, so our reader marked it and "
      + "nobody has checked that marking. This page was free.",
    mark: <Page size={28} />,
  },
  /* A corpus page. The same frame and the same type sizes as the others, and
     what separates it is what it says: where the marking came from, not how
     good it was. The standing cost is "free" because a corpus mark bills only
     where the operator has turned `corpus_marks_bill` on, and a caller that
     has read the service's `charged` passes the real answer over the top. */
  "corpus-marked": {
    title: "Marked from our corpus",
    cost: "free",
    body: "We have marked this question before, the same way twice, so we marked this page from "
      + "what we already hold rather than reading it again.",
    mark: <Tick size={28} />,
  },
  "topic-only": {
    title: "Topic found, not marked",
    cost: "free",
    body: "We know what the page is about, but we cannot mark this kind of question yet.",
    mark: <Page size={28} />,
  },
  "no-topic": {
    title: "No topic found",
    cost: "free",
    body: "The photograph is clear, and we still cannot work out the topic of this page. This page is free.",
    mark: <Magnifier size={28} />,
  },
  "too-soft": {
    title: "Nothing marked, photo too soft",
    cost: "free",
    body: "The photograph is ours to get right, so nothing needs writing out again.",
    mark: <SoftPhoto size={30} />,
  },
};

export function OutcomeCard({ outcome, children, detail, cost }: OutcomeCardProps) {
  const item = copy[outcome];
  const said = cost === undefined ? item.cost : cost;
  return (
    <article className="outcome">
      <div className="outcome-top">
        <span className="outcome-mark" aria-hidden="true">
          {item.mark}
        </span>
        {said ? <span className="outcome-cost">{said}</span> : null}
      </div>
      <h3 className="outcome-title">{item.title}</h3>
      {detail ? <p className="small">{detail}</p> : null}
      <p className="outcome-body">{children ?? item.body}</p>
    </article>
  );
}

/**
 * The five the marketing pages promise, in the contract's order: the two that
 * cost a credit first, then the three that cost nothing.
 *
 * It was four until the V1 wave. `no-topic` is a state a parent meets on a
 * result screen, and a page that arrives unannounced is the one a parent has
 * to work out for herself. The product call raised in the wave 0 merge report
 * is answered here in the direction the audit asked for: promise five, and say
 * that three of them cost nothing. The two headings that counted to four
 * changed with it.
 */
export const OUTCOMES: CardOutcome[] = [
  "explained", "no-cause", "topic-only", "no-topic", "too-soft",
];
